<script setup lang="ts">
import { ChevronLeft, ChevronRight, Home } from "lucide-vue-next";
import type { Worker } from "~/services/search";
import { formatPersonName, getWorkerProfilePath } from "~/utils/worker-profile";
import { SearchQueryParams, type SortBy } from "~/utils/search";
import { getCategoryIconPath } from "~/utils/category-icons";
import {
  SEARCH_PAGE_SIZE,
  catalogSlugToKey,
  getCategorySearchPath,
  getServiceSearchPath,
  getWorkersSearchPath,
  toTitleCase,
  type SearchCategoryOption,
  type SearchRegionOption,
  type SearchServiceOption,
} from "~/utils/search-catalog";

type SeoLink = {
  key: string;
  label: string;
  to: string | Record<string, unknown>;
};

type SeoCityBlock = {
  key: string;
  label: string;
  links: SeoLink[];
};

type SeoLinkGroup = {
  key: string;
  title: string;
  links: SeoLink[];
};

type LanguageMultiSelectOption = {
  id: number;
  label: string;
};

type SearchResponse = {
  workers: Worker[];
  totalCount: number;
};

type SearchFilterValues = {
  languageIds: number[];
  sort: "" | SortBy;
  priceMin: string;
  priceMax: string;
  experienceMin: string;
  experienceMax: string;
};

type BuildRouteQueryOptions = {
  page?: number;
  serviceName?: string;
  filters?: SearchFilterValues;
};

type SearchSeoCopyKind = keyof typeof SEARCH_SEO_COPY_KEYS;

const DEFAULT_SEO_LOCALE = "lt" as const;
const SEO_LOCALES = [
  { code: "lt", language: "lt", ogLocale: "lt_LT" },
  { code: "en", language: "en", ogLocale: "en_US" },
  { code: "ru", language: "ru", ogLocale: "ru_RU" },
] as const;

const NON_INDEXABLE_QUERY_KEYS = new Set<string>([
  SearchQueryParams.PageSize,
  SearchQueryParams.FilterCategory,
  SearchQueryParams.FilterService,
  SearchQueryParams.FilterLanguage,
  SearchQueryParams.FilterRegion,
  SearchQueryParams.FilterExperienceMin,
  SearchQueryParams.FilterExperienceMax,
  SearchQueryParams.FilterPriceMin,
  SearchQueryParams.FilterPriceMax,
  SearchQueryParams.SortBy,
  "category",
  "service",
  "city",
]);

const props = defineProps<{
  singleSlug?: string;
  firstSlug?: string;
  secondSlug?: string;
  thirdSlug?: string;
}>();

const route = useRoute();
const router = useRouter();
const localeRoute = useLocaleRoute();
const runtimeConfig = useRuntimeConfig();
const requestUrl = useRequestURL();
const { t, locale } = useI18n();

const ALL = "";
const SORT_OPTIONS: { value: "" | SortBy; labelKey: string }[] = [
  { value: "", labelKey: "search.sortbox.opt.default" },
  { value: "exp", labelKey: "search.sortbox.opt.experience" },
  { value: "hr_asc", labelKey: "search.sortbox.opt.hourly_rate_asc" },
  { value: "hr_desc", labelKey: "search.sortbox.opt.hourly_rate_desc" },
  { value: "rat", labelKey: "search.sortbox.opt.rating" },
  { value: "rev", labelKey: "search.sortbox.opt.reviews_count" },
];
const PRICE_SORT_VALUES = new Set(["hr_asc", "hr_desc"]);
const SEO_POPULAR_CITY_COUNT = 4;
const SEO_CITY_BLOCK_COUNT = 8;
const SEO_LINKS_PER_CITY = 7;

const categories = useState<SearchCategoryOption[]>(
  "workers-search-categories",
  () => [],
);
const regions = useState<SearchRegionOption[]>(
  "workers-search-regions",
  () => [],
);
const languages = useState<{ id: number; code: string }[]>(
  "workers-search-languages",
  () => [],
);
const services = useState<SearchServiceOption[]>(
  "workers-search-services",
  () => [],
);
const loadedServiceCategoryKeys = useState<string[]>(
  "workers-search-loaded-service-category-keys",
  () => [],
);

// Catalogs are stable during a SPA session. Keep them in Nuxt state so an SEO
// route change does not refetch categories/regions/languages on every page remount
await Promise.all([
  categories.value.length
    ? Promise.resolve()
    : $fetch<SearchCategoryOption[]>("/api/categories").then(value => {
        categories.value = value;
      }),
  regions.value.length
    ? Promise.resolve()
    : $fetch<SearchRegionOption[]>("/api/regions").then(value => {
        regions.value = value;
      }),
  languages.value.length
    ? Promise.resolve()
    : $fetch<{ id: number; code: string }[]>("/api/languages").then(value => {
        languages.value = value;
      }),
]);

function mergeServices(nextServices: SearchServiceOption[]) {
  const byId = new Map<number, SearchServiceOption>();

  services.value.forEach(item => byId.set(item.id, item));
  nextServices.forEach(item => byId.set(item.id, item));
  services.value = [...byId.values()].sort((a, b) => {
    if (a.categoryName !== b.categoryName) {
      const aCategory = categories.value.find(
        item => item.name === a.categoryName,
      )?.sortOrder;
      const bCategory = categories.value.find(
        item => item.name === b.categoryName,
      )?.sortOrder;
      const categoryOrder =
        Number(aCategory == null) - Number(bCategory == null) ||
        (aCategory ?? 0) - (bCategory ?? 0);

      return categoryOrder || a.categoryName.localeCompare(b.categoryName);
    }

    return (
      Number(a.sortOrder == null) - Number(b.sortOrder == null) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.name.localeCompare(b.name)
    );
  });
}

async function ensureServicesLoaded(categoryName?: string) {
  const key = categoryName || ALL;

  if (loadedServiceCategoryKeys.value.includes(key)) return;

  const nextServices = await $fetch<SearchServiceOption[]>("/api/services", {
    query: categoryName ? { categoryName } : undefined,
  });

  mergeServices(nextServices);
  loadedServiceCategoryKeys.value = [...loadedServiceCategoryKeys.value, key];
}

const selectedCategory = ref(ALL);
const selectedService = ref(ALL);
const selectedRegion = ref(ALL);
const draftCategory = ref(ALL);
const draftService = ref(ALL);
const draftRegion = ref(ALL);
const categorySearchText = ref("");
const serviceSearchText = ref("");
const regionSearchText = ref("");
const experienceMin = ref(ALL);
const experienceMax = ref(ALL);
const languageIds = ref<number[]>([]);
const sortBy = ref<"" | SortBy>(ALL);
const priceMin = ref("");
const priceMax = ref("");
const page = ref(1);

function getFirstQueryValue(value: unknown) {
  if (Array.isArray(value)) return value[0]?.toString() || "";

  return value?.toString() || "";
}

function parsePage(value: unknown) {
  const nextPage = Number(getFirstQueryValue(value));

  return Number.isInteger(nextPage) && nextPage > 0 ? nextPage : 1;
}

function translateCategory(value: string) {
  const key = `category.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

function translateCategoryShort(value: string) {
  const shortKey = `category_short.${value}`;
  const shortTranslated = t(shortKey);

  if (shortTranslated !== shortKey) return shortTranslated;

  return translateCategory(value);
}

function translateService(value: string) {
  const key = `services.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

function translateRegion(value: string) {
  const key = `region.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

function resolveRouteSegments() {
  const singleKey = catalogSlugToKey(props.singleSlug);
  const firstKey = catalogSlugToKey(props.firstSlug);
  const secondKey = catalogSlugToKey(props.secondSlug);
  const thirdKey = catalogSlugToKey(props.thirdSlug);

  let categoryKey = "";
  let serviceKey = "";
  let regionKey = "";

  if (singleKey && !firstKey && !secondKey && !thirdKey) {
    if (regions.value.some(item => item.name === singleKey)) {
      regionKey = singleKey;
    } else if (categories.value.some(item => item.name === singleKey)) {
      categoryKey = singleKey;
    }
  }

  if (firstKey && secondKey && thirdKey) {
    categoryKey = firstKey;
    serviceKey = secondKey;
    regionKey = thirdKey;
  } else if (firstKey && secondKey) {
    const secondIsRegion = regions.value.some(item => item.name === secondKey);

    categoryKey = firstKey;

    if (secondIsRegion) {
      regionKey = secondKey;
    } else {
      serviceKey = secondKey;
    }
  }

  const category = categories.value.find(item => item.name === categoryKey);
  const service = services.value.find(
    item => item.name === serviceKey && item.categoryName === categoryKey,
  );
  const region = regions.value.find(item => item.name === regionKey);

  return {
    categoryKey: category?.name || "",
    serviceKey: service?.name || "",
    regionKey: region?.name || "",
  };
}

function initFromRoute() {
  const {
    categoryKey: routeCategoryKey,
    serviceKey: routeServiceKey,
    regionKey: routeRegionKey,
  } = resolveRouteSegments();
  const regionKey =
    routeRegionKey ||
    catalogSlugToKey(
      getFirstQueryValue(route.query[SearchQueryParams.FilterRegion]),
    );
  const queryService = catalogSlugToKey(
    getFirstQueryValue(
      route.query[SearchQueryParams.FilterService] || route.query.service,
    ),
  );
  const queryCategory = catalogSlugToKey(
    getFirstQueryValue(
      route.query[SearchQueryParams.FilterCategory] || route.query.category,
    ),
  );

  const routeService = services.value.find(
    item =>
      item.name === routeServiceKey &&
      (!routeCategoryKey || item.categoryName === routeCategoryKey),
  );
  const routeCategory = categories.value.find(
    item => item.name === (routeCategoryKey || routeService?.categoryName),
  );
  const queryServiceItem = services.value.find(
    item => item.name === queryService,
  );
  const queryCategoryItem = categories.value.find(
    item => item.name === queryCategory,
  );
  const routeRegion = regions.value.find(item => item.name === regionKey);

  if (routeService) {
    selectedService.value = routeService.name;
    selectedCategory.value = routeService.categoryName;
  } else if (routeCategory) {
    selectedService.value = ALL;
    selectedCategory.value = routeCategory.name;
  } else if (queryServiceItem) {
    selectedService.value = queryServiceItem.name;
    selectedCategory.value = queryServiceItem.categoryName;
  } else if (queryCategoryItem) {
    selectedService.value = ALL;
    selectedCategory.value = queryCategoryItem.name;
  } else {
    selectedService.value = ALL;
    selectedCategory.value = ALL;
  }

  const queryRegion = regions.value.find(
    item =>
      item.name === catalogSlugToKey(getFirstQueryValue(route.query.city)),
  );

  selectedRegion.value = routeRegion?.name || queryRegion?.name || ALL;
  page.value = parsePage(route.query[SearchQueryParams.Page]);
  syncDraftFilters();
  syncHeaderSearchText();
}

function findOptionLabel(
  options: { value: string; label: string }[],
  value: string,
) {
  if (!value) return "";

  return options.find(option => option.value === value)?.label || "";
}

function syncDraftFilters() {
  draftCategory.value = selectedCategory.value;
  draftService.value = selectedService.value;
  draftRegion.value = selectedRegion.value;
  const filters = appliedFilters.value;
  experienceMin.value = filters.experienceMin;
  experienceMax.value = filters.experienceMax;
  languageIds.value = [...filters.languageIds];
  sortBy.value = filters.sort;
  priceMin.value = filters.priceMin;
  priceMax.value = filters.priceMax;
}

function syncHeaderSearchText() {
  categorySearchText.value = findOptionLabel(
    categoryOptions.value,
    draftCategory.value,
  );
  serviceSearchText.value = findOptionLabel(
    serviceOptions.value,
    draftService.value,
  );
  regionSearchText.value = findOptionLabel(
    regionOptions.value,
    draftRegion.value,
  );
}

const categoryOptions = computed(() =>
  categories.value.map(item => ({
    value: item.name,
    label: translateCategory(item.name),
  })),
);

const serviceOptions = computed(() =>
  services.value.map(item => ({
    value: item.name,
    label: translateService(item.name),
    categoryName: item.categoryName,
  })),
);

const regionOptions = computed(() =>
  regions.value.map(item => ({
    value: item.name,
    label: translateRegion(item.name),
  })),
);

const languageOptions = computed<LanguageMultiSelectOption[]>(() =>
  languages.value.map(item => ({
    id: item.id,
    label: t(`language.${item.code}`),
  })),
);

function getLanguageCodes(ids: number[]) {
  const selectedIdSet = new Set(ids);

  return languages.value
    .filter(item => selectedIdSet.has(item.id))
    .map(item => item.code);
}

const selectedLanguageText = computed(() => {
  const count = languageIds.value.length;

  if (!count) return t("search.filters.language.any");
  if (count === 1) {
    const option = languageOptions.value.find(
      item => item.id === languageIds.value[0],
    );

    return option?.label || t("search.filters.language.any");
  }

  return t("search.filters.language.selected", { count });
});

function languageCodesToIds(codes: string[]) {
  const codeSet = new Set(codes);

  return languages.value
    .filter(item => codeSet.has(item.code))
    .map(item => item.id);
}

const appliedFilters = computed<SearchFilterValues>(() => {
  const languageQuery = route.query[SearchQueryParams.FilterLanguage];
  const languageCodes = Array.isArray(languageQuery)
    ? languageQuery.map(item => catalogSlugToKey(item?.toString()))
    : [catalogSlugToKey(getFirstQueryValue(languageQuery))].filter(Boolean);
  const sortValue = getFirstQueryValue(route.query[SearchQueryParams.SortBy]);
  const sort = SORT_OPTIONS.some(item => item.value === sortValue)
    ? (sortValue as "" | SortBy)
    : ALL;

  return {
    languageIds: languageCodesToIds(languageCodes),
    sort: !selectedService.value && PRICE_SORT_VALUES.has(sort) ? ALL : sort,
    priceMin: selectedService.value
      ? getFirstQueryValue(route.query[SearchQueryParams.FilterPriceMin])
      : ALL,
    priceMax: selectedService.value
      ? getFirstQueryValue(route.query[SearchQueryParams.FilterPriceMax])
      : ALL,
    experienceMin: getFirstQueryValue(
      route.query[SearchQueryParams.FilterExperienceMin],
    ),
    experienceMax: getFirstQueryValue(
      route.query[SearchQueryParams.FilterExperienceMax],
    ),
  };
});

const selectedLanguageCodes = computed(() =>
  getLanguageCodes(appliedFilters.value.languageIds),
);

function getFilterValues(): SearchFilterValues {
  return {
    languageIds: languageIds.value,
    sort: sortBy.value,
    priceMin: priceMin.value,
    priceMax: priceMax.value,
    experienceMin: experienceMin.value,
    experienceMax: experienceMax.value,
  };
}

const sortOptions = computed(() =>
  SORT_OPTIONS.filter(
    item => selectedService.value || !PRICE_SORT_VALUES.has(item.value),
  ).map(item => ({ value: item.value, label: t(item.labelKey) })),
);

const showPriceFilter = computed(() => Boolean(draftService.value));

function getRouteCategoryKey() {
  return (
    resolveRouteSegments().categoryKey ||
    catalogSlugToKey(
      getFirstQueryValue(
        route.query[SearchQueryParams.FilterCategory] || route.query.category,
      ),
    )
  );
}

const initialRouteCategory = getRouteCategoryKey();

if (initialRouteCategory) {
  await ensureServicesLoaded(initialRouteCategory);
}

initFromRoute();

const selectedCatalogLabel = computed(() => {
  if (selectedService.value) return translateService(selectedService.value);
  if (selectedCategory.value) return translateCategory(selectedCategory.value);

  return t("search.page.default_catalog_title");
});

const selectedCategoryLabel = computed(() => {
  return selectedCategory.value
    ? translateCategory(selectedCategory.value)
    : "";
});

const selectedServiceLabel = computed(() => {
  return selectedService.value ? translateService(selectedService.value) : "";
});

const selectedRegionLabel = computed(() =>
  selectedRegion.value
    ? translateRegion(selectedRegion.value)
    : t("search.page.lithuania"),
);

function buildSearchApiQuery() {
  const filters = appliedFilters.value;
  const query: Record<string, string | number | string[]> = {
    [SearchQueryParams.Page]: page.value,
    [SearchQueryParams.PageSize]: SEARCH_PAGE_SIZE,
  };

  if (selectedService.value) {
    query[SearchQueryParams.FilterService] = selectedService.value;
  } else if (selectedCategory.value) {
    query[SearchQueryParams.FilterCategory] = selectedCategory.value;
  }

  if (selectedRegion.value) {
    query[SearchQueryParams.FilterRegion] = selectedRegion.value;
  }

  const languageCodes = getLanguageCodes(filters.languageIds);
  if (languageCodes.length) {
    query[SearchQueryParams.FilterLanguage] = languageCodes;
  }

  if (filters.sort) query[SearchQueryParams.SortBy] = filters.sort;

  if (selectedService.value && filters.priceMin) {
    query[SearchQueryParams.FilterPriceMin] = filters.priceMin;
  }

  if (selectedService.value && filters.priceMax) {
    query[SearchQueryParams.FilterPriceMax] = filters.priceMax;
  }

  if (filters.experienceMin) {
    query[SearchQueryParams.FilterExperienceMin] = Number(
      filters.experienceMin,
    );
  }

  if (filters.experienceMax) {
    query[SearchQueryParams.FilterExperienceMax] = Number(
      filters.experienceMax,
    );
  }

  return query;
}

const searchApiQuery = computed(() => buildSearchApiQuery());

const {
  data,
  error,
  refresh: refreshSearch,
} = await useFetch<SearchResponse>("/api/search", {
  method: "GET",
  query: searchApiQuery,
  watch: false,
  default: () => ({ workers: [], totalCount: 0 }),
});

const workers = computed(() => data.value?.workers || []);
const total = computed(() => data.value?.totalCount || 0);
const lastPage = computed(() =>
  Math.max(1, Math.ceil(total.value / SEARCH_PAGE_SIZE)),
);
const firstVisible = computed(() =>
  total.value ? (page.value - 1) * SEARCH_PAGE_SIZE + 1 : 0,
);
const lastVisible = computed(() =>
  Math.min(total.value, page.value * SEARCH_PAGE_SIZE),
);
const routeNavigationLoading = useState<boolean>(
  "workers-search-route-loading",
  () => false,
);
const searchRefreshPending = ref(false);
const isSearchLoading = computed(
  () =>
    import.meta.client &&
    (routeNavigationLoading.value || searchRefreshPending.value),
);
const hasNoExactResults = computed(
  () => !error.value && !isSearchLoading.value && !workers.value.length,
);
const isFilteredEmptyState = computed(
  () => hasNoExactResults.value && hasNonIndexableQuery.value,
);
const pageNumbers = computed(() => {
  const maxVisible = 5;
  const half = Math.floor(maxVisible / 2);
  const start = Math.max(
    1,
    Math.min(page.value - half, lastPage.value - maxVisible + 1),
  );
  const end = Math.min(lastPage.value, start + maxVisible - 1);

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
});

function makeCategorySeoLink(
  categoryKey: string,
  regionKey: string | undefined,
  label: string,
): SeoLink {
  const path = getCategorySearchPath(categoryKey, regionKey);

  return {
    key: `${categoryKey}-${regionKey || "all"}`,
    label,
    to: localeRoute(path) || path,
  };
}

function makeServiceSeoLink(
  service: SearchServiceOption,
  regionKey: string | undefined,
  label: string,
): SeoLink {
  const path = getServiceSearchPath(
    service.categoryName,
    service.name,
    regionKey,
  );

  return {
    key: `${service.categoryName}-${service.name}-${regionKey || "all"}`,
    label,
    to: localeRoute(path) || path,
  };
}

function makeRegionSeoLink(regionKey: string, label: string): SeoLink {
  const path = getWorkersSearchPath(regionKey);

  return {
    key: `workers-${regionKey}`,
    label,
    to: localeRoute(path) || path,
  };
}

function dedupeSeoLinks(links: SeoLink[]) {
  const seen = new Set<string>();

  return links.filter(link => {
    const key = `${link.key}-${link.label}`;

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

const citySeoBlocks = computed<SeoCityBlock[]>(() => {
  const selectedRegionItem = regions.value.find(
    item => item.name === selectedRegion.value,
  );
  const popularRegions = regions.value.slice(0, SEO_POPULAR_CITY_COUNT);
  const selectedAndPopular = [
    ...(selectedRegionItem ? [selectedRegionItem] : []),
    ...popularRegions.filter(item => item.name !== selectedRegion.value),
  ];
  const remainingRegions = regions.value.filter(
    item => !selectedAndPopular.some(region => region.name === item.name),
  );
  const rotateFrom = selectedRegionItem
    ? Math.max(
        0,
        regions.value.findIndex(item => item.name === selectedRegionItem.name) -
          SEO_POPULAR_CITY_COUNT,
      )
    : Math.max(0, page.value - 1);
  const rotatedRemaining = remainingRegions.length
    ? [
        ...remainingRegions.slice(rotateFrom % remainingRegions.length),
        ...remainingRegions.slice(0, rotateFrom % remainingRegions.length),
      ]
    : [];
  const blockRegions = [...selectedAndPopular, ...rotatedRemaining].slice(
    0,
    SEO_CITY_BLOCK_COUNT,
  );
  const selectedServiceItem = services.value.find(
    service => service.name === selectedService.value,
  );
  const catalogCategory =
    selectedCategory.value || selectedServiceItem?.categoryName || "";

  return blockRegions.map(region => {
    const regionLabel = translateRegion(region.name);
    const links: SeoLink[] = [
      makeRegionSeoLink(
        region.name,
        t("search.page.workers_in_city", { city: regionLabel }),
      ),
    ];

    if (selectedServiceItem) {
      links.push(
        makeServiceSeoLink(
          selectedServiceItem,
          region.name,
          selectedServiceLabel.value,
        ),
      );
    }

    if (catalogCategory) {
      links.push(
        makeCategorySeoLink(
          catalogCategory,
          region.name,
          translateCategory(catalogCategory),
        ),
      );

      services.value
        .filter(
          service =>
            service.categoryName === catalogCategory &&
            service.name !== selectedService.value,
        )
        .slice(0, 5)
        .forEach(service => {
          links.push(
            makeServiceSeoLink(
              service,
              region.name,
              translateService(service.name),
            ),
          );
        });
    } else {
      categories.value.slice(0, 6).forEach(category => {
        links.push(
          makeCategorySeoLink(
            category.name,
            region.name,
            translateCategoryShort(category.name),
          ),
        );
      });
    }

    return {
      key: region.name,
      label: regionLabel,
      links: dedupeSeoLinks(links).slice(0, SEO_LINKS_PER_CITY),
    };
  });
});

const categoryCarouselLinks = computed(() => {
  const selectedServiceItem = services.value.find(
    service => service.name === selectedService.value,
  );
  const preferredCategory =
    selectedCategory.value || selectedServiceItem?.categoryName || "";
  const orderedCategories = [
    ...categories.value.filter(item => item.name === preferredCategory),
    ...categories.value.filter(item => item.name !== preferredCategory),
  ];
  const regionName = selectedRegion.value || undefined;

  return orderedCategories.map(category => ({
    ...makeCategorySeoLink(
      category.name,
      regionName,
      translateCategoryShort(category.name),
    ),
    icon: getCategoryIconPath(category.name),
  }));
});

const categoryCarouselTitle = computed(() =>
  selectedRegion.value
    ? t("search.page.explore_categories_in_city", {
        city: selectedRegionLabel.value,
      })
    : t("search.page.explore_categories"),
);

const seoLinkGroups = computed<SeoLinkGroup[]>(() => {
  const selectedServiceItem = services.value.find(
    service => service.name === selectedService.value,
  );
  const catalogCategory =
    selectedCategory.value || selectedServiceItem?.categoryName || "";

  const locationLinks = citySeoBlocks.value.map(block => {
    const city = block.label;

    if (selectedServiceItem) {
      return makeServiceSeoLink(
        selectedServiceItem,
        block.key,
        t("search.page.catalog_in_city", {
          catalog: selectedServiceLabel.value,
          city,
        }),
      );
    }

    if (catalogCategory) {
      return makeCategorySeoLink(
        catalogCategory,
        block.key,
        t("search.page.catalog_in_city", {
          catalog: translateCategoryShort(catalogCategory),
          city,
        }),
      );
    }

    return makeRegionSeoLink(
      block.key,
      t("search.page.professionals_in_city", { city }),
    );
  });

  const relatedLinks = catalogCategory
    ? services.value
        .filter(
          service =>
            service.categoryName === catalogCategory &&
            service.name !== selectedService.value,
        )
        .slice(0, 10)
        .map(service =>
          makeServiceSeoLink(
            service,
            selectedRegion.value || undefined,
            translateService(service.name),
          ),
        )
    : categories.value
        .slice(0, 10)
        .map(category =>
          makeCategorySeoLink(
            category.name,
            selectedRegion.value || undefined,
            translateCategoryShort(category.name),
          ),
        );

  const groups: SeoLinkGroup[] = [
    {
      key: "locations",
      title: hasCatalogSelection.value
        ? t("search.page.catalog_in_lithuania", {
            catalog: selectedCatalogLabel.value,
          })
        : t("search.page.professionals_in_lithuania"),
      links: dedupeSeoLinks(locationLinks),
    },
  ];

  if (relatedLinks.length) {
    groups.push({
      key: "related",
      title: hasCatalogSelection.value
        ? t("search.page.related_services_from_similar")
        : t("search.page.related_professional_categories"),
      links: dedupeSeoLinks(relatedLinks),
    });
  }

  return groups.filter(group => group.links.length);
});

const breadcrumbs = computed(() => {
  const items: {
    label: string;
    to: string | ReturnType<typeof localeRoute>;
  }[] = [
    {
      label: t("search.page.home"),
      to: localeRoute("/") || "/",
    },
  ];

  if (!selectedCategory.value && !selectedService.value) {
    items.push({
      label: t("search.page.workers"),
      to: localeRoute(getWorkersSearchPath()) || getWorkersSearchPath(),
    });
  }

  if (selectedCategory.value) {
    const categoryPath = getCategorySearchPath(selectedCategory.value);

    items.push({
      label: selectedCategoryLabel.value,
      to: localeRoute(categoryPath) || categoryPath,
    });
  }

  if (selectedService.value) {
    const servicePath = getServiceSearchPath(
      selectedCategory.value,
      selectedService.value,
    );

    items.push({
      label: selectedServiceLabel.value,
      to: localeRoute(servicePath) || servicePath,
    });
  }

  if (selectedRegion.value) {
    const cityPath = getWorkersSearchPath(selectedRegion.value);

    items.push({
      label: selectedRegionLabel.value,
      to: localeRoute(cityPath) || cityPath,
    });
  }

  return items;
});

function resolveSiteOrigin(value: unknown) {
  const configuredUrl = String(value || "").trim();

  if (!configuredUrl) {
    if (import.meta.dev) return requestUrl.origin;

    throw createError({
      statusCode: 500,
      statusMessage: "NUXT_PUBLIC_SITE_URL is required",
    });
  }

  try {
    return new URL(configuredUrl).origin;
  } catch {
    throw createError({
      statusCode: 500,
      statusMessage: "NUXT_PUBLIC_SITE_URL must be a valid absolute URL",
    });
  }
}

function serializeJsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

function absoluteUrl(path: string) {
  return new URL(path, `${siteUrl}/`).toString();
}

const siteUrl = resolveSiteOrigin(runtimeConfig.public.siteUrl);
const currentLocale = computed(
  () =>
    SEO_LOCALES.find(item => item.code === locale.value) ||
    SEO_LOCALES.find(item => item.code === DEFAULT_SEO_LOCALE) ||
    SEO_LOCALES[0],
);
const hasCatalogSelection = computed(() =>
  Boolean(selectedCategory.value || selectedService.value),
);
const hasRegionSelection = computed(() => Boolean(selectedRegion.value));
const SEARCH_SEO_COPY_KEYS = {
  title: {
    default: "search.page.seo.title_default",
    catalog: "search.page.seo.title_catalog",
    region: "search.page.seo.title_region",
    catalogRegion: "search.page.seo.title_catalog_region",
  },
  description: {
    default: "search.page.seo.description_default",
    catalog: "search.page.seo.description_catalog",
    region: "search.page.seo.description_region",
    catalogRegion: "search.page.seo.description_catalog_region",
  },
  intro: {
    default: "search.page.seo.intro_default",
    catalog: "search.page.seo.intro_catalog",
    region: "search.page.seo.intro_region",
    catalogRegion: "search.page.seo.intro_catalog_region",
  },
} as const;

function getSearchSeoCopy(kind: SearchSeoCopyKind) {
  const keys = SEARCH_SEO_COPY_KEYS[kind];

  if (hasCatalogSelection.value && hasRegionSelection.value) {
    return t(keys.catalogRegion, {
      catalog: selectedCatalogLabel.value,
      region: selectedRegionLabel.value,
    });
  }

  if (hasCatalogSelection.value) {
    return t(keys.catalog, { catalog: selectedCatalogLabel.value });
  }

  if (hasRegionSelection.value) {
    return t(keys.region, { region: selectedRegionLabel.value });
  }

  return t(keys.default);
}

const pageTitle = computed(() => getSearchSeoCopy("title"));
const documentTitle = computed(() =>
  page.value > 1
    ? `${pageTitle.value} – ${t("search.page.seo.page_suffix", { page: page.value })}`
    : pageTitle.value,
);
const pageDescription = computed(() => getSearchSeoCopy("description"));
const documentDescription = computed(() =>
  page.value > 1
    ? `${pageDescription.value} ${t("search.page.seo.page_context", { page: page.value })}`
    : pageDescription.value,
);
const pageIntroDescription = computed(() => getSearchSeoCopy("intro"));

const canonicalSearchPath = computed(() => getSearchPath());
const canonicalPageSuffix = computed(() =>
  page.value > 1 ? `?${SearchQueryParams.Page}=${page.value}` : "",
);
const localizedCanonicalPath = computed(
  () =>
    localeRoute(canonicalSearchPath.value)?.path || canonicalSearchPath.value,
);
const canonicalUrl = computed(
  () => `${siteUrl}${localizedCanonicalPath.value}${canonicalPageSuffix.value}`,
);
const localizedUrls = computed(() =>
  SEO_LOCALES.map(item => {
    const path =
      localeRoute(canonicalSearchPath.value, item.code)?.path ||
      canonicalSearchPath.value;

    return {
      ...item,
      url: `${siteUrl}${path}${canonicalPageSuffix.value}`,
    };
  }),
);
const hasNonIndexableQuery = computed(() =>
  Object.keys(route.query).some(key => NON_INDEXABLE_QUERY_KEYS.has(key)),
);
const hasInvalidPagination = computed(
  () => page.value > 1 && (total.value === 0 || page.value > lastPage.value),
);
const isIndexableSearchPage = computed(
  () => !hasNonIndexableQuery.value && !hasInvalidPagination.value,
);
const robotsValue = computed(() =>
  isIndexableSearchPage.value ? "index, follow" : "noindex, follow",
);
function resolveBreadcrumbUrl(
  to: string | ReturnType<typeof localeRoute>,
  isLast: boolean,
) {
  if (isLast) return canonicalUrl.value;

  const resolved = router.resolve(to || "/");
  return absoluteUrl(resolved.href);
}

const structuredBreadcrumbItems = computed(() =>
  breadcrumbs.value.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.label,
    item: resolveBreadcrumbUrl(
      crumb.to,
      index === breadcrumbs.value.length - 1,
    ),
  })),
);
const structuredWorkerItems = computed(() =>
  workers.value.map((worker, index) => {
    const profilePath = getWorkerProfilePath(worker);
    const localizedPath = localeRoute(profilePath)?.path || profilePath;
    const name = formatPersonName(worker.name, worker.lastname);

    return {
      "@type": "ListItem",
      position: (page.value - 1) * SEARCH_PAGE_SIZE + index + 1,
      url: absoluteUrl(localizedPath),
      ...(name ? { name } : {}),
    };
  }),
);
const structuredData = computed(() => {
  const collectionId = `${canonicalUrl.value}#collection`;
  const listId = `${canonicalUrl.value}#professionals`;
  const breadcrumbsId = `${canonicalUrl.value}#breadcrumbs`;
  const hasExactWorkers = workers.value.length > 0;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": collectionId,
        url: canonicalUrl.value,
        name: documentTitle.value,
        description: documentDescription.value,
        inLanguage: currentLocale.value.language,
        breadcrumb: { "@id": breadcrumbsId },
        ...(hasExactWorkers ? { mainEntity: { "@id": listId } } : {}),
      },
      ...(hasExactWorkers
        ? [
            {
              "@type": "ItemList",
              "@id": listId,
              numberOfItems: workers.value.length,
              itemListElement: structuredWorkerItems.value,
            },
          ]
        : []),
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbsId,
        itemListElement: structuredBreadcrumbItems.value,
      },
    ],
  };
});

useSeoMeta({
  title: () => documentTitle.value,
  description: () => documentDescription.value,
  robots: () => robotsValue.value,
  ogTitle: () => documentTitle.value,
  ogDescription: () => documentDescription.value,
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
  twitterCard: "summary",
  twitterTitle: () => documentTitle.value,
  twitterDescription: () => documentDescription.value,
});

useHead(() => ({
  htmlAttrs: {
    lang: currentLocale.value.language,
  },
  link: [
    { rel: "canonical", href: canonicalUrl.value },
    ...(isIndexableSearchPage.value
      ? [
          ...localizedUrls.value.map(item => ({
            rel: "alternate",
            hreflang: item.language,
            href: item.url,
          })),
          {
            rel: "alternate",
            hreflang: "x-default",
            href:
              localizedUrls.value.find(item => item.code === DEFAULT_SEO_LOCALE)
                ?.url || canonicalUrl.value,
          },
        ]
      : []),
  ],
  script: [
    {
      type: "application/ld+json",
      key: "workers-search-jsonld",
      innerHTML: serializeJsonLd(structuredData.value),
    },
  ],
}));

function buildRouteQuery({
  page: nextPage = page.value,
  serviceName = selectedService.value,
  filters = appliedFilters.value,
}: BuildRouteQueryOptions = {}) {
  const query: Record<string, string | string[]> = {};
  const languageCodes = getLanguageCodes(filters.languageIds);

  // Category/service/region are encoded in the SEO path
  // (/workers/{category}/{service}/{city}), so query keeps only extra filters.

  if (languageCodes.length) {
    query[SearchQueryParams.FilterLanguage] = languageCodes;
  }
  if (filters.sort) query[SearchQueryParams.SortBy] = filters.sort;

  if (serviceName && filters.priceMin) {
    query[SearchQueryParams.FilterPriceMin] = filters.priceMin;
  }

  if (serviceName && filters.priceMax) {
    query[SearchQueryParams.FilterPriceMax] = filters.priceMax;
  }

  if (filters.experienceMin) {
    query[SearchQueryParams.FilterExperienceMin] = filters.experienceMin;
  }

  if (filters.experienceMax) {
    query[SearchQueryParams.FilterExperienceMax] = filters.experienceMax;
  }

  if (nextPage > 1) query[SearchQueryParams.Page] = String(nextPage);

  return query;
}

async function navigateSearch(location: {
  path: string;
  query: Record<string, string | string[]>;
}) {
  const resolvedLocation = localeRoute(location) || location;

  await router.push(resolvedLocation);
}

function getSearchPath(
  categoryName = selectedCategory.value,
  serviceName = selectedService.value,
  regionName = selectedRegion.value,
) {
  if (categoryName && serviceName) {
    return getServiceSearchPath(
      categoryName,
      serviceName,
      regionName || undefined,
    );
  }

  if (categoryName) {
    return getCategorySearchPath(categoryName, regionName || undefined);
  }

  return getWorkersSearchPath(regionName || undefined);
}

function scrollPageToTop() {
  if (!import.meta.client || window.scrollY <= 1) return Promise.resolve();

  const startY = window.scrollY;
  const duration = Math.min(220, Math.max(110, 90 + startY * 0.045));
  const startTime = performance.now();

  return new Promise<void>(resolve => {
    const scroll = (time: number) => {
      const progress = Math.min((time - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, startY * (1 - easedProgress));

      if (progress < 1) {
        window.requestAnimationFrame(scroll);
        return;
      }

      window.scrollTo(0, 0);
      resolve();
    };

    window.requestAnimationFrame(scroll);
  });
}

async function submitSearch() {
  const nextCategory = draftCategory.value;
  const nextService = draftService.value;
  const nextRegion = draftRegion.value;
  const location = {
    path: getSearchPath(nextCategory, nextService, nextRegion),
    query: buildRouteQuery({
      page: 1,
      serviceName: nextService,
      filters: getFilterValues(),
    }),
  };

  syncHeaderSearchText();
  await scrollPageToTop();
  await navigateSearch(location);
}

async function handleSimpleFilterChange() {
  const filters = getFilterValues();
  const location = {
    path: getSearchPath(),
    query: buildRouteQuery({ page: 1, filters }),
  };

  await scrollPageToTop();
  await navigateSearch(location);
}

async function resetFilters() {
  await scrollPageToTop();

  draftCategory.value = ALL;
  draftService.value = ALL;
  draftRegion.value = ALL;
  experienceMin.value = ALL;
  experienceMax.value = ALL;
  languageIds.value = [];
  sortBy.value = ALL;
  priceMin.value = ALL;
  priceMax.value = ALL;
  syncHeaderSearchText();
  await navigateSearch({ path: getWorkersSearchPath(), query: {} });
}

function getPaginationLocation(nextPage: number) {
  const location = {
    path: getSearchPath(),
    query: buildRouteQuery({ page: nextPage }),
  };

  return localeRoute(location) || location;
}

async function handlePaginationClick(event: MouseEvent, nextPage: number) {
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();
  await scrollPageToTop();
  await router.push(getPaginationLocation(nextPage));
}

watch(draftCategory, async value => {
  if (value) {
    await ensureServicesLoaded(value);
  }

  const service = services.value.find(item => item.name === draftService.value);

  if (!value) {
    draftService.value = ALL;
    serviceSearchText.value = "";
    return;
  }

  if (draftService.value && service?.categoryName !== value) {
    draftService.value = ALL;
    serviceSearchText.value = "";
  }
});

function resetPriceFilters() {
  priceMin.value = ALL;
  priceMax.value = ALL;

  if (PRICE_SORT_VALUES.has(sortBy.value)) {
    sortBy.value = ALL;
  }
}

watch(draftService, value => {
  const service = services.value.find(item => item.name === value);

  // Price belongs to a concrete service. If the user changes the draft service
  // before submitting the main search, do not carry a price from the previously
  // applied service into the new one.
  if (value !== selectedService.value) {
    resetPriceFilters();
  }

  if (service) {
    draftCategory.value = service.categoryName;
    categorySearchText.value = findOptionLabel(
      categoryOptions.value,
      service.categoryName,
    );
  }
});

async function ensureRouteCatalogLoaded() {
  const routeCategory = getRouteCategoryKey();

  if (routeCategory) {
    await ensureServicesLoaded(routeCategory);
  }
}

async function refreshSearchResults() {
  searchRefreshPending.value = true;
  try {
    await ensureRouteCatalogLoaded();
    initFromRoute();
    await refreshSearch();
  } finally {
    searchRefreshPending.value = false;
  }
}

watch(
  () => [route.name, route.fullPath] as const,
  async ([nextRouteName], [previousRouteName]) => {
    if (nextRouteName !== previousRouteName) return;

    await refreshSearchResults();
  },
);
</script>

<template>
  <main class="bg-white">
    <WorkersSearchHeader
      v-model:category-value="draftCategory"
      v-model:category-search-text="categorySearchText"
      v-model:service-value="draftService"
      v-model:service-search-text="serviceSearchText"
      v-model:region-value="draftRegion"
      v-model:region-search-text="regionSearchText"
      v-model:experience-min-value="experienceMin"
      v-model:experience-max-value="experienceMax"
      v-model:price-min-value="priceMin"
      v-model:price-max-value="priceMax"
      v-model:language-ids="languageIds"
      :category-options="categoryOptions"
      :service-options="serviceOptions"
      :region-options="regionOptions"
      :language-options="languageOptions"
      :selected-language-text="selectedLanguageText"
      :show-price-filter="showPriceFilter"
      :loading="isSearchLoading"
      @submit="submitSearch"
      @load-services="
        ensureServicesLoaded(draftCategory || selectedCategory || undefined)
      "
      @filter="handleSimpleFilterChange"
      @reset="resetFilters"
    />

    <section
      class="mx-auto w-full max-w-[1480px] px-4 pb-16 pt-5 md:pb-20 md:pt-6"
    >
      <div class="max-w-[860px]">
        <h1 class="text-2xl font-bold tracking-tight text-app-text md:text-3xl">
          {{ documentTitle }}
        </h1>

        <p class="mt-1.5 max-w-[820px] text-sm leading-[1.5] text-app-muted">
          {{ pageIntroDescription }}
        </p>

        <nav
          class="mt-3 flex flex-wrap items-center gap-2 text-sm text-app-muted"
        >
          <template v-for="(crumb, index) in breadcrumbs" :key="crumb.label">
            <NuxtLink
              v-if="crumb.to && index < breadcrumbs.length - 1"
              :to="crumb.to"
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-medium text-primary transition duration-300 hover:text-primary-700"
            >
              <Home v-if="index === 0" class="size-3.5" />
              {{ crumb.label }}
            </NuxtLink>
            <span
              v-else
              class="shrink-0 whitespace-nowrap font-semibold text-app-text"
            >
              {{ crumb.label }}
            </span>
            <span
              v-if="index < breadcrumbs.length - 1"
              class="shrink-0 text-primary/35"
            >
              /
            </span>
          </template>
        </nav>
      </div>

      <div
        class="mt-5 grid grid-cols-1 gap-6 min-[1360px]:grid-cols-[minmax(0,1fr)_300px]"
      >
        <section class="min-w-0">
          <div
            v-if="total"
            class="mb-4 flex items-center justify-between gap-3"
          >
            <div class="text-sm font-semibold text-app-text">
              {{
                t("search.page.showing", {
                  from: firstVisible,
                  to: lastVisible,
                  total,
                })
              }}
            </div>

            <div class="w-[176px] shrink-0 sm:w-[196px]">
              <AppSelect
                v-model="sortBy"
                :options="sortOptions"
                align="right"
                width="md"
                option-variant="radio"
                :sheet-title="t('search.sortbox.title')"
                trigger-class="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-app-border bg-white px-4 text-left text-sm font-semibold transition duration-300 hover:border-primary/35 hover:bg-primary-50 hover:text-primary"
                open-trigger-class="!border-primary/35 !bg-primary-50/80 !text-primary"
                @update:model-value="handleSimpleFilterChange"
              />
            </div>
          </div>

          <div
            v-if="error"
            class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-app-danger"
          >
            {{ t("search.status.error") }}
          </div>

          <WorkersSearchEmptyState
            v-else-if="hasNoExactResults"
            :title="
              isFilteredEmptyState
                ? t('search.status.no_filter_results_title')
                : t('search.status.empty_results_title')
            "
            :description="
              isFilteredEmptyState
                ? t('search.status.no_filter_results_description')
                : t('search.status.empty_landing_description')
            "
            :action-label="
              isFilteredEmptyState
                ? t('search.reset_filters')
                : t('search.reset_search')
            "
            @reset="resetFilters"
          />

          <div
            v-if="workers.length || isSearchLoading"
            class="relative"
            :aria-busy="isSearchLoading"
          >
            <div
              v-if="workers.length"
              class="flex flex-col gap-4 transition duration-200"
              :class="isSearchLoading ? 'pointer-events-none opacity-55' : ''"
            >
              <SearchCard
                v-for="worker in workers"
                :key="worker.id"
                :worker="worker"
                :selected-service-name="selectedService || undefined"
                :selected-category-name="selectedCategory || undefined"
                :selected-language-codes="selectedLanguageCodes"
                :region-name="selectedRegion"
              />
            </div>

            <WorkersSearchResultsLoader
              v-if="isSearchLoading"
              :label="t('search.status.loading_results')"
              :hint="t('search.status.loading_results_hint')"
              :standalone="!workers.length"
            />
          </div>

          <nav
            v-if="total > SEARCH_PAGE_SIZE"
            class="mt-6 flex items-center justify-center gap-1"
            aria-label="Pagination"
          >
            <NuxtLink
              v-if="page > 1"
              :to="getPaginationLocation(page - 1)"
              rel="prev"
              class="flex size-9 items-center justify-center rounded-lg border border-app-border transition duration-300 hover:bg-gray-50"
              @click.capture="handlePaginationClick($event, page - 1)"
            >
              <ChevronLeft class="size-4" />
            </NuxtLink>
            <span
              v-else
              aria-hidden="true"
              class="flex size-9 cursor-not-allowed items-center justify-center rounded-lg border border-app-border opacity-40"
            >
              <ChevronLeft class="size-4" />
            </span>

            <template v-for="pageNumber in pageNumbers" :key="pageNumber">
              <span
                v-if="pageNumber === page"
                :aria-current="'page'"
                class="flex size-9 items-center justify-center rounded-lg border border-primary bg-primary text-sm font-semibold text-white"
              >
                {{ pageNumber }}
              </span>
              <NuxtLink
                v-else
                :to="getPaginationLocation(pageNumber)"
                class="flex size-9 items-center justify-center rounded-lg border border-app-border text-sm font-semibold transition duration-300 hover:bg-gray-50"
                @click.capture="handlePaginationClick($event, pageNumber)"
              >
                {{ pageNumber }}
              </NuxtLink>
            </template>

            <NuxtLink
              v-if="page < lastPage"
              :to="getPaginationLocation(page + 1)"
              rel="next"
              class="flex size-9 items-center justify-center rounded-lg border border-app-border transition duration-300 hover:bg-gray-50"
              @click.capture="handlePaginationClick($event, page + 1)"
            >
              <ChevronRight class="size-4" />
            </NuxtLink>
            <span
              v-else
              aria-hidden="true"
              class="flex size-9 cursor-not-allowed items-center justify-center rounded-lg border border-app-border opacity-40"
            >
              <ChevronRight class="size-4" />
            </span>
          </nav>
        </section>

        <BannersProfessionalPromo />
      </div>

      <!-- Category discovery stays close to the search results. -->
      <WorkersCategoryCarousel
        :title="categoryCarouselTitle"
        :links="categoryCarouselLinks"
      />

      <!-- Safety guidance follows category discovery. -->
      <BannersSafetyBanner class="mt-10" />

      <!-- Compact SEO link groups close the page. -->
      <WorkersSeoLinkGroups :groups="seoLinkGroups" />
    </section>
  </main>
</template>
